import type React from "react";
import { useState, useEffect, useCallback } from "react";

import { useNextSongSelector, playNextSong } from "@redux/player";
import { useDispatch } from "react-redux";
import { useBoolean } from "react-use";

import { manualTimeUpdateEvent } from "components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.constants";
import { isMobile } from "services/helpers/browser";
import { useMediaElementListener } from "services/hooks/useMediaElementListener";
import { useVideoIsPlaying } from "services/hooks/useVideoIsPlaying";

import DesktopControls from "../DesktopControls";
import MobileControls from "../MobileControls";

interface Props {
	handlePlay: () => void;
	isFullscreen: boolean;
	toggleFullscreen: () => void;
	videoElement: HTMLVideoElement;
}

const Mp3KbpPlayerControls: React.FC<Props> = ({
	handlePlay,
	isFullscreen,
	toggleFullscreen,
	videoElement,
}) => {
	const dispatch = useDispatch();
	const nextSong = useNextSongSelector();
	const [songDuration, setSongDuration] = useState<number | null>(null);
	const [songFutureTime, setSongFutureTime] = useState<number | null>(null);
	const [bufferedTimeRanges, setBufferedTimeRanges] =
		useState<TimeRanges | null>(null);
	const [isMuted, toggleIsMuted] = useBoolean(false);
	const [currentVolume, setCurrentVolume] = useState<number>(1);
	const [lastPositiveVolume, setLastPositiveVolume] = useState<number>(1);
	const isPlaying = useVideoIsPlaying(videoElement);

	useEffect(() => {
		if (
			!videoElement ||
			songFutureTime === null ||
			songFutureTime === undefined
		) {
			return;
		}
		videoElement.currentTime = songFutureTime;
	}, [videoElement, songFutureTime]);

	useMediaElementListener(videoElement, "durationchange", () => {
		if (!videoElement) {
			return;
		}
		setSongDuration(videoElement.duration);
	});

	const handlePause = useCallback(async () => {
		if (!videoElement) {
			return;
		}
		videoElement.pause();
	}, [videoElement]);

	const handleMute = useCallback(() => {
		if (!videoElement) {
			return;
		}

		if (currentVolume === 0) {
			// this handles the case when the volume cursor
			// was first dragged to 0 (--> currentVolume === 0)
			// and then the user clicks on "demute":
			// we reset the currentVolume to the last positive
			// value observed
			videoElement.volume = lastPositiveVolume;
			return;
		}

		// Else, clicking the "mute" button toggles the player muted state
		videoElement.muted = !videoElement.muted;
	}, [videoElement, lastPositiveVolume, currentVolume]);

	useMediaElementListener(videoElement, "progress", () => {
		if (!videoElement) {
			return;
		}
		setBufferedTimeRanges(videoElement.buffered);
	});

	useMediaElementListener(videoElement, "volumechange", () => {
		if (!videoElement) {
			return;
		}
		toggleIsMuted(videoElement.muted);
		setCurrentVolume(videoElement.volume);
	});

	const handleVolumeChange = useCallback(
		(volume: number) => {
			if (!videoElement) {
				return;
			}
			videoElement.volume = volume;
			if (volume > 0) {
				// this is necessary to handle the case when the player was muted
				// and the user drags the volume up
				videoElement.muted = false;
			}
		},
		[videoElement],
	);

	const handleRestartSong = useCallback(() => {
		if (!videoElement) {
			return;
		}
		videoElement.currentTime = 0;
		videoElement.dispatchEvent(manualTimeUpdateEvent);
	}, [videoElement]);

	const handlePlayNextSong = useCallback(() => {
		if (!videoElement) {
			return;
		}
		dispatch(playNextSong());
		videoElement.dispatchEvent(manualTimeUpdateEvent);
	}, [dispatch, videoElement]);

	const isPlayNextSongDisabled = nextSong === undefined || nextSong === null;

	return isMobile() ? (
		<MobileControls
			isPlaying={isPlaying}
			handlePlay={handlePlay}
			handlePause={handlePause}
			handleRestartSong={handleRestartSong}
			isPlayNextSongDisabled={isPlayNextSongDisabled}
			handlePlayNextSong={handlePlayNextSong}
			songDuration={songDuration}
			songFutureTime={songFutureTime}
			setSongFutureTime={setSongFutureTime}
			bufferedTimeRanges={bufferedTimeRanges}
			isFullscreen={isFullscreen}
			toggleFullscreen={toggleFullscreen}
			videoElement={videoElement}
		/>
	) : (
		<DesktopControls
			isPlaying={isPlaying}
			handlePlay={handlePlay}
			handlePause={handlePause}
			handleRestartSong={handleRestartSong}
			isPlayNextSongDisabled={isPlayNextSongDisabled}
			handlePlayNextSong={handlePlayNextSong}
			songDuration={songDuration}
			songFutureTime={songFutureTime}
			setSongFutureTime={setSongFutureTime}
			bufferedTimeRanges={bufferedTimeRanges}
			isMuted={isMuted}
			handleMute={handleMute}
			currentVolume={currentVolume}
			handleVolumeChange={handleVolumeChange}
			setLastPositiveVolume={setLastPositiveVolume}
			isFullscreen={isFullscreen}
			toggleFullscreen={toggleFullscreen}
			videoElement={videoElement}
		/>
	);
};

export default Mp3KbpPlayerControls;
